import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { WEBHOOK_EVENT_LABELS, type AutomationHubEventType } from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { ResizableWorkspace } from '@/components/workspace/ResizableWorkspace';
import { AutomationEditor } from './AutomationEditor';
import { AutomationRunDetailPanel } from './AutomationRunDetailPanel';
import { AutomationTemplateGallery } from './AutomationTemplateGallery';
import styles from './automations.module.css';

type AutomationRow = {
  id: string;
  name: string;
  enabled: boolean;
  trigger_type: string;
  trigger_config: { event_type?: string };
  last_run_status: string | null;
};

type Props = {
  projectId: string;
};

export function AutomationsWorkspace({ projectId }: Props) {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ['automations', projectId],
    queryFn: () =>
      apiFetch<{ automations: AutomationRow[] }>(`/api/automations?project_id=${projectId}`),
  });

  const templatesQ = useQuery({
    queryKey: ['automation-templates', projectId],
    queryFn: () =>
      apiFetch<{
        templates: {
          key: string;
          name: string;
          description: string;
          params: { key: string; label: string; type: string; required?: boolean }[];
        }[];
      }>(`/api/automation-templates?project_id=${projectId}`),
  });

  const agentsQ = useQuery({
    queryKey: ['agents', projectId],
    queryFn: () =>
      apiFetch<{ agents: { id: string; name: string; enabled: boolean }[] }>(
        `/api/agents?project_id=${projectId}`,
      ),
  });

  const toggleM = useMutation({
    mutationFn: (row: AutomationRow) =>
      apiFetch(`/api/automations/${row.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !row.enabled }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['automations', projectId] }),
  });

  const detailOpen = Boolean(selectedId || isNew);

  return (
    <>
      <AutomationTemplateGallery
        projectId={projectId}
        templates={templatesQ.data?.templates ?? []}
        agents={agentsQ.data?.agents ?? []}
        onCreated={(id) => {
          setIsNew(false);
          setSelectedId(id);
        }}
      />
      {runId ? <AutomationRunDetailPanel runId={runId} onClose={() => setRunId(null)} /> : null}
      <ResizableWorkspace
        storageKey="mh-automations-detail-width"
        detailOpen={detailOpen}
        onDetailDismiss={() => {
          setSelectedId(null);
          setIsNew(false);
        }}
        main={
          <aside className={styles.listPanel}>
            <Button
              type="button"
              onClick={() => {
                setIsNew(true);
                setSelectedId(null);
              }}
            >
              + Workflow
            </Button>
            {(listQ.data?.automations ?? []).map((a) => (
              <div key={a.id} style={{ display: 'flex', gap: '0.35rem', alignItems: 'stretch' }}>
                <button
                  type="button"
                  className={[
                    styles.listBtn,
                    selectedId === a.id && !isNew ? styles.listBtnActive : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{ flex: 1 }}
                  onClick={() => {
                    setSelectedId(a.id);
                    setIsNew(false);
                  }}
                >
                  <strong>{a.name}</strong>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-slate)' }}>
                    {a.trigger_type === 'event'
                      ? WEBHOOK_EVENT_LABELS[
                          (a.trigger_config.event_type ?? 'lead.created') as AutomationHubEventType
                        ]
                      : a.trigger_type}
                    {' · '}
                    {a.enabled ? 'Aan' : 'Uit'}
                    {a.last_run_status ? ` · ${a.last_run_status}` : ''}
                  </div>
                </button>
                <Button type="button" variant="ghost" onClick={() => toggleM.mutate(a)}>
                  {a.enabled ? 'Uit' : 'Aan'}
                </Button>
              </div>
            ))}
          </aside>
        }
        detail={
          detailOpen ? (
            <AutomationEditor
              projectId={projectId}
              automationId={selectedId}
              isNew={isNew}
              onSaved={(id) => {
                setSelectedId(id);
                setIsNew(false);
              }}
              onRunSelect={setRunId}
            />
          ) : null
        }
      />
    </>
  );
}
